import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { videoApi } from '../services/api';
import { auth } from '../lib/firebase';
import { useAuth } from './AuthContext';

export interface UploadTask {
  id: string;
  fileName: string;
  fileSize: number;
  status: 'uploading' | 'analyzing' | 'completed' | 'error';
  progress: number;
  uploadStatus: string;
  videoId?: string;
  error?: string;
  startTime: Date;
  canCancel: boolean;
  processingStatus?: string; // Additional status for video processing
  isProcessingExisting?: boolean; // Flag to identify processing tasks added from dashboard
  autoRemoveScheduled?: boolean; // Flag to mark tasks for auto-removal
  source?: 'file' | 'url'; // Source of the upload: file upload or URL import
  platform?: 'youtube' | 'twitch' | 'kick'; // Platform for URL imports
}

interface UploadContextType {
  tasks: UploadTask[];
  addTask: (file: File) => string;
  addUrlImportTask: (url: string, platform: 'youtube' | 'twitch' | 'kick') => string;
  addProcessingVideo: (videoId: string, fileName: string, fileSize: number) => string;
  updateTask: (id: string, updates: Partial<UploadTask>) => void;
  updateTaskFromVideoStatus: (videoId: string, statusData: any) => void;
  removeTask: (id: string) => void;
  cancelTask: (id: string) => void;
  hasActiveTasks: boolean;
  clearCompletedTasks: () => void;
  getSyntheticProgress: (videoId: string) => number;
  updateSyntheticProgress: (videoId: string, targetProgress: number, message: string) => void;
  clearSyntheticProgress: (videoId: string) => void;
  clearAllStuckStates: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};

interface UploadProviderProps {
  children: ReactNode;
}

// Global tracking for processing videos to prevent duplicates across components
const PROCESSING_VIDEOS_KEY = 'global_processing_videos';

const getGlobalProcessingVideos = (): Set<string> => {
  try {
    const stored = localStorage.getItem(PROCESSING_VIDEOS_KEY);
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
};

const addToGlobalProcessingVideos = (videoId: string): void => {
  const processing = getGlobalProcessingVideos();
  processing.add(videoId);
  localStorage.setItem(PROCESSING_VIDEOS_KEY, JSON.stringify(Array.from(processing)));
};

const removeFromGlobalProcessingVideos = (videoId: string): void => {
  const processing = getGlobalProcessingVideos();
  processing.delete(videoId);
  localStorage.setItem(PROCESSING_VIDEOS_KEY, JSON.stringify(Array.from(processing)));
};

const isVideoBeingProcessed = (videoId: string): boolean => {
  return getGlobalProcessingVideos().has(videoId);
};

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<UploadTask[]>(() => {
    // Load tasks from localStorage on initial load with enhanced stale task cleanup
    try {
      const savedTasks = localStorage.getItem('upload_tasks');
      if (savedTasks) {
        const parsed = JSON.parse(savedTasks);
        const now = Date.now();
        
        // Filter out old tasks with more aggressive cleanup
        const filteredTasks = parsed
          .map((task: any) => ({
            ...task,
            startTime: new Date(task.startTime)
          }))
          .filter((task: UploadTask) => {
            const taskAge = now - task.startTime.getTime();
            
            // Remove tasks older than 24 hours automatically (prevents week-old stuck states)
            if (taskAge > 86400000) {
              // console.log(`🧹 Clearing stale task on app load: ${task.fileName} (${Math.round(taskAge / 3600000)}h old)`);
              return false;
            }
            
            // Keep active tasks and recent completed/error tasks (within last hour)
            return task.status === 'uploading' || 
                   task.status === 'analyzing' || 
                   taskAge < 3600000; // 1 hour for completed/error tasks
          });
        
        // If we filtered out tasks, also clean up related localStorage entries
        if (filteredTasks.length < parsed.length) {
          // console.log(`🧹 Cleaned up ${parsed.length - filteredTasks.length} stale tasks on app load`);
          
          // Clear potentially related stale entries
          Object.keys(localStorage).forEach(key => {
            if (key.includes('synthetic_progress') || 
                key.includes('video_recent_') || 
                key.includes('recent_task_')) {
              try {
                const entryData = localStorage.getItem(key);
                if (entryData) {
                  // Try to parse and check if it's old
                  const data = JSON.parse(entryData);
                  if (data.timestamp && (now - data.timestamp) > 86400000) {
                    localStorage.removeItem(key);
                    // console.log(`🧹 Cleared stale localStorage entry: ${key}`);
                  }
                }
              } catch (e) {
                // If parsing fails, remove old entries that might be corrupted
                localStorage.removeItem(key);
              }
            }
          });
        }
        
        return filteredTasks;
      }
    } catch (error) {
      // console.error('Error loading tasks from localStorage:', error);
      // If there's an error, clear the potentially corrupted data
      localStorage.removeItem('upload_tasks');
    }
    return [];
  });

  const restorationAttemptedRef = useRef(false);
  const { currentUser } = useAuth();

  // Restore active processing videos when the app loads - ONLY if user is authenticated AND has potential tasks
  useEffect(() => {
    const restoreProcessingVideos = async () => {
      // Only attempt restoration if user is authenticated and we haven't tried yet
      if (!currentUser || restorationAttemptedRef.current) return;
      
      // First check localStorage to see if we have any processing tasks that might need restoration
      const hasPotentialProcessingTasks = tasks.some(task => 
        task.status === 'analyzing' && task.videoId && task.isProcessingExisting
      );
      
      // Also check if we have any recent upload tasks that might have become processing tasks
      const hasRecentUploadTasks = tasks.some(task => 
        task.status === 'uploading' || 
        (task.status === 'analyzing' && !task.isProcessingExisting)
      );
      
      // Check if user has uploaded anything recently (within last 24 hours)
      const hasRecentUploads = (() => {
        const lastUpload = localStorage.getItem('has_recent_uploads');
        if (!lastUpload) return false;
        const lastUploadTime = parseInt(lastUpload);
        return (Date.now() - lastUploadTime) < 86400000; // 24 hours
      })();
      
      // Check if we've already checked recently (within last hour)
      const hasRecentCheck = (() => {
        const lastCheck = localStorage.getItem('last_restoration_check');
        if (!lastCheck) return false;
        const lastCheckTime = parseInt(lastCheck);
        return (Date.now() - lastCheckTime) < 3600000; // 1 hour
      })();
      
      // Only make API call if:
      // 1. We have potential tasks to restore, OR
      // 2. User has uploaded recently and we haven't checked in the last hour, OR  
      // 3. This is the first time ever checking
      const shouldCheckForProcessingVideos = hasPotentialProcessingTasks || 
                                           hasRecentUploadTasks || 
                                           (hasRecentUploads && !hasRecentCheck) ||
                                           !localStorage.getItem('last_restoration_check');
      
      if (!shouldCheckForProcessingVideos) {
        restorationAttemptedRef.current = true;
        // console.log('🚫 [UploadContext] Skipping restoration check - no recent activity or already checked recently');
        return;
      }
      
      restorationAttemptedRef.current = true;
      
      try {
        // Double-check that user is still authenticated before making API call
        if (!currentUser || !auth.currentUser) {
          // console.log('🚫 [UploadContext] User no longer authenticated, skipping restoration');
          return;
        }

        // Ensure user token is valid before making API call
        try {
          await currentUser.getIdToken();
        } catch (tokenError) {
          // console.log('🚫 [UploadContext] Failed to get auth token, skipping restoration');
          return;
        }

        // console.log('🔄 [UploadContext] Checking for active processing videos to restore...');
        const videos = await videoApi.getVideos();
        
        // Ensure videos is an array before filtering
        if (!Array.isArray(videos)) {
          // console.error('[UploadContext] Videos API returned non-array:', typeof videos);
          return;
        }
        
        // Find videos that are currently processing
        const processingVideos = videos.filter(video => 
          video.status === 'processing' || video.status === 'uploaded'
        );
        
        if (processingVideos.length > 0) {
          // console.log(`🔄 [UploadContext] Found ${processingVideos.length} processing videos to restore`);
          
          // Add tasks for processing videos that aren't already tracked
          const newTasks: UploadTask[] = [];
          processingVideos.forEach(video => {
            const existingTask = tasks.find(task => task.videoId === video.id);
            if (!existingTask) {
              // Also check localStorage to prevent cross-session duplicates
              const recentTasksKey = 'recent_processing_tasks';
              const recentTasks = JSON.parse(localStorage.getItem(recentTasksKey) || '{}');
              const now = Date.now();
              
              // Skip if we recently created a task for this video (within last 10 minutes)
              if (recentTasks[video.id] && (now - recentTasks[video.id]) < 600000) {
                // console.log(`🔄 [UploadContext] Skipping restoration for ${video.id} - recent task found in localStorage`);
                return;
              }
              
              // console.log(`🔄 [UploadContext] Restoring processing video: ${video.filename} (${video.id})`);
              
              const taskId = `processing_${video.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const newTask: UploadTask = {
                id: taskId,
                fileName: video.filename,
                fileSize: video.metadata?.file_size || 0,
                status: video.status === 'processing' ? 'analyzing' : 'analyzing',
                progress: 90,
                uploadStatus: 'Restored from background processing...',
                videoId: video.id,
                startTime: new Date(),
                canCancel: true,
                isProcessingExisting: true,
                processingStatus: video.status === 'processing' ? 'Analyzing video content...' : 'Starting analysis...'
              };
              
              newTasks.push(newTask);
              
              // Mark in localStorage to prevent duplicates
              recentTasks[video.id] = now;
              localStorage.setItem(recentTasksKey, JSON.stringify(recentTasks));
            }
          });
          
          if (newTasks.length > 0) {
            setTasks(prev => [...newTasks, ...prev]);
            // console.log(`✅ [UploadContext] Restored ${newTasks.length} processing tasks`);
          }
        }
        
        // Mark that we've checked for this session
        localStorage.setItem('last_restoration_check', Date.now().toString());
        
        // console.log(`✅ [UploadContext] Restoration complete. Found ${processingVideos.length} processing videos.`);
      } catch (error: any) {
        // Handle authentication errors gracefully
        if (error?.response?.status === 401 || error?.message?.includes('Authentication required')) {
          // console.log('🔐 [UploadContext] Authentication required for restoration, skipping');
        } else {
          // console.error('❌ [UploadContext] Error restoring processing videos:', error);
        }
        // Don't show user-facing error for this background restoration
      }
    };

    // Reset restoration flag when user changes
    if (!currentUser) {
      restorationAttemptedRef.current = false;
      // Clear the restoration check when user logs out
      localStorage.removeItem('last_restoration_check');
    }

    // Run restoration when user is authenticated
    if (currentUser) {
      restoreProcessingVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Remove 'tasks' from dependency to prevent loops

  // Auto-remove completed tasks after 20 seconds
  useEffect(() => {
    const completedTasks = tasks.filter(task => 
      task.status === 'completed' && !task.autoRemoveScheduled
    );
    
    completedTasks.forEach(task => {
      // Mark as scheduled to prevent duplicate timers
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, autoRemoveScheduled: true } : t
      ));
      
      // Remove after 20 seconds
      setTimeout(() => {
        setTasks(prev => {
          const filtered = prev.filter(t => t.id !== task.id);
          // console.log(`🗑️ [UploadContext] Auto-removed completed task: ${task.fileName}`);
          return filtered;
        });
      }, 20000); // 20 seconds
    });
  }, [tasks]);

  // Save tasks to localStorage whenever tasks change
  useEffect(() => {
    try {
      localStorage.setItem('upload_tasks', JSON.stringify(tasks));
    } catch (error) {
      // console.error('Error saving tasks to localStorage:', error);
    }
  }, [tasks]);

  // Cleanup old localStorage entries periodically
  useEffect(() => {
    const cleanup = () => {
      try {
        // Clean up old upload activity markers (older than 7 days)
        const hasRecentUploads = localStorage.getItem('has_recent_uploads');
        if (hasRecentUploads) {
          const lastUploadTime = parseInt(hasRecentUploads);
          if (Date.now() - lastUploadTime > 604800000) { // 7 days
            localStorage.removeItem('has_recent_uploads');
          }
        }
        
        // Clean up old restoration checks (older than 7 days)
        const lastCheck = localStorage.getItem('last_restoration_check');
        if (lastCheck) {
          const lastCheckTime = parseInt(lastCheck);
          if (Date.now() - lastCheckTime > 604800000) { // 7 days
            localStorage.removeItem('last_restoration_check');
          }
        }
      } catch (error) {
        // console.error('Error cleaning up localStorage:', error);
      }
    };
    
    // Run cleanup on mount and then every hour
    cleanup();
    const interval = setInterval(cleanup, 3600000); // 1 hour
    
    return () => clearInterval(interval);
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<UploadTask>) => {
    // console.log(`🔄 UpdateTask called for ${id}:`, updates);
    
    setTasks(prev => {
      const updatedTasks = prev.map(task => {
        if (task.id === id) {
          const updatedTask = { ...task, ...updates };
          // console.log(`📝 Task ${id} updated:`, {
          //   fileName: updatedTask.fileName,
          //   status: updatedTask.status,
          //   progress: updatedTask.progress,
          //   uploadStatus: updatedTask.uploadStatus,
          //   processingStatus: updatedTask.processingStatus
          // });
          return updatedTask;
        }
        return task;
      });
      
      return updatedTasks;
    });
  }, []);

  // Poll processing status for uploaded videos - DISABLED since we now use unified polling
  // The unified polling system handles all video status updates
  /*
  useEffect(() => {
    const processingTasks = tasks.filter(task => 
      task.status === 'analyzing' && task.videoId
    );

    if (processingTasks.length === 0) return;

    // Track consecutive errors for exponential backoff
    let consecutiveErrors = 0;
    let abortController: AbortController | null = null;
    let currentTimeout: NodeJS.Timeout | null = null;

    const scheduleNextPoll = (delay: number = 10000) => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      currentTimeout = setTimeout(performPoll, delay);
    };

    const performPoll = async () => {
      // ... polling logic disabled ...
    };

    // Start polling immediately
    performPoll();

    // Cleanup function
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      if (abortController) {
        abortController.abort();
      }
    };
  }, [tasks]);
  */

  const addProcessingVideo = useCallback((videoId: string, fileName: string, fileSize: number) => {
    // First check global tracking to prevent duplicates across all components
    if (isVideoBeingProcessed(videoId)) {
      // console.log(`🚫 [UploadContext] Video ${videoId} already being processed globally, skipping duplicate`);
      return `existing_global_${videoId}`;
    }
    
    // Check if task already exists for this video (more comprehensive check)
    const existingTask = tasks.find(task => 
      task.videoId === videoId && 
      (task.status === 'uploading' || task.status === 'analyzing' || 
       (task.status === 'completed' && (Date.now() - task.startTime.getTime()) < 60000)) // Within last 60 seconds
    );
    
    if (existingTask) {
      // console.log(`Task already exists for video ${videoId}, returning existing task ID: ${existingTask.id}`);
      return existingTask.id;
    }
    
    // Additional check: Look in localStorage for recently created tasks to prevent cross-session duplicates
    const recentTasksKey = 'recent_processing_tasks';
    try {
      const recentTasks = JSON.parse(localStorage.getItem(recentTasksKey) || '{}');
      const now = Date.now();
      
      // Clean old entries (older than 5 minutes)
      Object.keys(recentTasks).forEach(id => {
        if (now - recentTasks[id] > 300000) {
          delete recentTasks[id];
        }
      });
      
      // Check if we already have a recent task for this video
      if (recentTasks[videoId] && (now - recentTasks[videoId]) < 300000) {
        // console.log(`Recent task found for video ${videoId} in localStorage, skipping duplicate`);
        localStorage.setItem(recentTasksKey, JSON.stringify(recentTasks));
        return `existing_${videoId}`;
      }
      
      // Add this video to recent tasks
      recentTasks[videoId] = now;
      localStorage.setItem(recentTasksKey, JSON.stringify(recentTasks));
    } catch (error) {
      // console.error('Error checking recent tasks in localStorage:', error);
    }
    
    // Add to global tracking to prevent other components from duplicating
    addToGlobalProcessingVideos(videoId);
    
    // Use a more unique key to prevent React key collisions
    const taskId = `processing_${videoId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newTask: UploadTask = {
      id: taskId,
      fileName,
      fileSize,
      status: 'analyzing',
      progress: 5, // Start at 5% to match synthetic progress
      uploadStatus: 'Starting video analysis...',
      videoId,
      startTime: new Date(),
      canCancel: true, // Allow canceling video processing
      isProcessingExisting: true,
      processingStatus: 'Starting video analysis...'
    };
    
    setTasks(prev => {
      // Double-check to prevent race conditions
      const existingInPrev = prev.find(task => 
        task.videoId === videoId && 
        (task.status === 'uploading' || task.status === 'analyzing')
      );
      
      if (existingInPrev) {
        // console.log(`Race condition detected - task already exists for video ${videoId}`);
        return prev; // Don't add duplicate
      }
      
      // console.log(`Adding processing task for video ${videoId}: ${taskId}`);
      return [newTask, ...prev];
    });
    
    // Mark that user has recent upload activity (since they're processing a video)
    localStorage.setItem('has_recent_uploads', Date.now().toString());
    
    return taskId;
  }, [tasks]);

  const addUrlImportTask = useCallback((url: string, platform: 'youtube' | 'twitch' | 'kick'): string => {
    // Extract filename from URL for display purposes
    const _urlObj = new URL(url);
    let fileName = `${platform}.com video`;
    
    // Try to extract video ID or title from URL for better display
    if (platform === 'youtube') {
      let videoId = '';
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
      } else if (url.includes('watch?v=')) {
        videoId = url.split('watch?v=')[1]?.split('&')[0] || '';
      }
      fileName = videoId ? `YouTube Video (${videoId})` : 'YouTube Video';
    } else if (platform === 'twitch') {
      fileName = 'Twitch Video';
    } else if (platform === 'kick') {
      fileName = 'Kick Video';
    }
    
    // Check if a similar URL import task already exists
    const existingTask = tasks.find(task => 
      task.fileName === fileName && 
      (task.status === 'uploading' || task.status === 'analyzing')
    );
    
    if (existingTask) {
      // Return existing task ID to prevent duplicate
      return existingTask.id;
    }

    // Create new URL import task
    const taskId = `url_import_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newTask: UploadTask = {
      id: taskId,
      fileName,
      fileSize: 0, // Unknown size initially
      status: 'uploading',
      progress: 5, // Start at 5% for immediate feedback
      uploadStatus: 'Starting URL import...',
      startTime: new Date(),
      canCancel: false, // URL imports typically can't be cancelled once started
      isProcessingExisting: false,
      source: 'url',
      platform
    };
    
    setTasks(prev => [newTask, ...prev]);
    
    // Mark that user has recent upload activity
    localStorage.setItem('has_recent_uploads', Date.now().toString());
    
    return taskId;
  }, [tasks]);

  const addTask = useCallback((file: File): string => {
    // Check if a task for this file already exists and is still active
    const fileKey = `${file.name}_${file.size}`;
    const existingTask = tasks.find(task => 
      `${task.fileName}_${task.fileSize}` === fileKey && 
      (task.status === 'uploading' || task.status === 'analyzing') &&
      !task.isProcessingExisting // Don't match processing tasks
    );
    
    if (existingTask) {
      // Return existing task ID to prevent duplicate
      return existingTask.id;
    }

    // Also check for recently completed tasks (within last 10 seconds) to prevent immediate re-uploads
    const recentCompletedTask = tasks.find(task => 
      `${task.fileName}_${task.fileSize}` === fileKey && 
      task.status === 'completed' &&
      (Date.now() - task.startTime.getTime()) < 10000 &&
      !task.isProcessingExisting
    );
    
    if (recentCompletedTask) {
      // Return existing task ID to prevent duplicate
      return recentCompletedTask.id;
    }

    // Check for existing task with same file but different status
    const anyExistingTask = tasks.find(task => 
      `${task.fileName}_${task.fileSize}` === fileKey &&
      !task.isProcessingExisting
    );
    
    if (anyExistingTask) {
      // If it's an error task or old completed task, we can reuse the ID
      if (anyExistingTask.status === 'error' || 
          (anyExistingTask.status === 'completed' && (Date.now() - anyExistingTask.startTime.getTime()) > 10000)) {
        // Update the existing task instead of creating a new one
        setTasks(prev => prev.map(task => 
          task.id === anyExistingTask.id 
            ? {
                ...task,
                status: 'uploading',
                progress: 0,
                uploadStatus: 'Initializing...',
                startTime: new Date(),
                canCancel: true,
                error: undefined,
                processingStatus: undefined,
                isProcessingExisting: false
              }
            : task
        ));
        
        // Mark that user has recent upload activity
        localStorage.setItem('has_recent_uploads', Date.now().toString());
        
        return anyExistingTask.id;
      }
    }

    // Create new task
    const taskId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newTask: UploadTask = {
      id: taskId,
      fileName: file.name,
      fileSize: file.size,
      status: 'uploading',
      progress: 0,
      uploadStatus: 'Initializing...',
      startTime: new Date(),
      canCancel: true,
      isProcessingExisting: false,
      source: 'file'
    };
    
    setTasks(prev => [newTask, ...prev]);
    
    // Mark that user has recent upload activity
    localStorage.setItem('has_recent_uploads', Date.now().toString());
    
    return taskId;
  }, [tasks]);

  const removeTask = useCallback(async (id: string) => {
    // Find the task before removing to clean up global tracking
    const task = tasks.find(t => t.id === id);
    if (task && task.videoId && task.isProcessingExisting) {
      removeFromGlobalProcessingVideos(task.videoId);
    }
    
    // Check if this is a stuck processing task that might need backend cleanup
    if (task && task.videoId && task.isProcessingExisting && 
        (task.fileName === 'processing.mp4' || task.status === 'analyzing')) {
      
      // Attempt to clean up stuck videos from backend
      try {
        // console.log(`🧹 [UploadContext] Attempting to clean up stuck backend videos when removing task ${task.id}`);
        const { videoApi } = await import('../services/api');
        await videoApi.cleanupStuckVideos();
        // console.log(`✅ [UploadContext] Backend cleanup completed successfully`);
        toast.success('Cleaned up stuck processing videos');
      } catch (error) {
        // console.error('Backend cleanup failed:', error);
        // Don't show error toast - the task removal should still work
      }
    }
    
    setTasks(prev => prev.filter(task => task.id !== id));
  }, [tasks]);

  const cancelTask = useCallback(async (id: string) => {
    // Find the task to get video ID if it exists
    const task = tasks.find(t => t.id === id);
    
    // Clean up global tracking for processing videos
    if (task && task.videoId && task.isProcessingExisting) {
      removeFromGlobalProcessingVideos(task.videoId);
    }
    
    // If task has a video ID and is analyzing, call backend to cancel
    if (task && task.videoId && task.status === 'analyzing') {
      // Call backend to cancel video processing
      try {
        const { videoApi } = await import('../services/api');
        const response = await videoApi.cancelVideoProcessing(task.videoId);
        // console.log(`Successfully cancelled video processing for ${task.videoId}`);
        
        // Check if the backend deleted a placeholder video
        if (response && response.status === 'deleted') {
          toast.info('Stuck video removed');
        } else {
          toast.info('Video processing cancelled');
        }
        
        // Also attempt cleanup in case of stuck videos
        if (task.fileName === 'processing.mp4' || task.isProcessingExisting) {
          try {
            await videoApi.cleanupStuckVideos();
            // console.log(`✅ [UploadContext] Backend cleanup completed after cancellation`);
          } catch (cleanupError) {
            // console.error('Backend cleanup after cancellation failed:', cleanupError);
            // Don't show error - cancellation worked
          }
        }
      } catch (error: any) {
        // console.error(`Failed to cancel video processing:`, error);
        // Handle 404 errors gracefully for placeholder videos
        if (error?.response?.status === 404) {
          toast.info('Video already removed');
        } else {
          toast.error('Failed to cancel processing');
        }
      }
    }
    
    // Update task status locally
    setTasks(prev => prev.map(task => 
      task.id === id 
        ? { ...task, status: 'error', error: 'Cancelled by user', canCancel: false }
        : task
    ));
    
    // Only show toast if we didn't already show one above
    if (!task || !task.videoId || task.status !== 'analyzing') {
      toast.info('Upload cancelled');
    }
  }, [tasks]);

  const _hasActiveTasks = tasks.some(task => 
    task.status === 'uploading' || task.status === 'analyzing'
  );

  const clearCompletedTasks = useCallback(() => {
    setTasks(prev => prev.filter(task => task.status !== 'completed'));
  }, []);

  // Helper function to parse progress from error_message
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parseProgressFromMessage = (errorMessage: string): { progress: number; message: string } => {
    if (!errorMessage) {
      // console.log(`🔍 [UploadContext] No error message provided, using default`);
      return { progress: 5, message: 'Starting video analysis...' };
    }
    
    // Log the incoming message for debugging
    // console.log(`🔍 [UploadContext] Parsing progress from: "${errorMessage}"`);
    
    // Look for percentage pattern like "25.0% - message" or "95.6% - Generating clip 4/5"
    const progressMatch = errorMessage.match(/^(\d+(?:\.\d+)?)%\s*[-–—]\s*(.+)$/);
    if (progressMatch) {
      const progress = parseFloat(progressMatch[1]);
      const message = progressMatch[2].trim();
      // console.log(`✅ [UploadContext] Parsed progress: ${progress}%, message: "${message}"`);
      return { progress, message };
    }
    
    // Also try to match patterns like "95.6% Generating clip" (without dash)
    const progressMatch2 = errorMessage.match(/^(\d+(?:\.\d+)?)%\s+(.+)$/);
    if (progressMatch2) {
      const progress = parseFloat(progressMatch2[1]);
      const message = progressMatch2[2].trim();
      // console.log(`✅ [UploadContext] Parsed progress (alt): ${progress}%, message: "${message}"`);
      return { progress, message };
    }
    
    // Try to extract percentage from anywhere in the message
    const anyProgressMatch = errorMessage.match(/(\d+(?:\.\d+)?)%/);
    if (anyProgressMatch) {
      const progress = parseFloat(anyProgressMatch[1]);
      // console.log(`✅ [UploadContext] Found progress anywhere: ${progress}%`);
      return { progress, message: errorMessage };
    }
    
    // Special handling for common status messages without percentages
    const lowerMessage = errorMessage.toLowerCase();
    if (lowerMessage.includes('complete') || lowerMessage.includes('finished') || lowerMessage.includes('done')) {
      return { progress: 100, message: errorMessage };
    }
    if (lowerMessage.includes('starting') || lowerMessage.includes('initializing')) {
      return { progress: 5, message: errorMessage };
    }
    if (lowerMessage.includes('analyzing') || lowerMessage.includes('processing')) {
      return { progress: 25, message: errorMessage };
    }
    if (lowerMessage.includes('generating') || lowerMessage.includes('creating')) {
      return { progress: 75, message: errorMessage };
    }
    
    // console.log(`⚠️ [UploadContext] No progress found in: "${errorMessage}"`);
    // If no percentage found, return the message as-is with minimum progress
    return { progress: 5, message: errorMessage };
  };

  // Add synthetic progress tracking system for smooth 0-100% progress
  const [syntheticProgress, setSyntheticProgress] = useState<Record<string, { progress: number; lastUpdate: number; intervalId?: NodeJS.Timeout; targetProgress?: number; lastBackendUpdate?: number }>>({});
  const syntheticProgressIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const fallbackProgressIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const updateSyntheticProgress = useCallback((videoId: string, targetProgress: number, message: string) => {
    // console.log(`🎯 [UploadContext] updateSyntheticProgress called: ${videoId} -> ${targetProgress}% - ${message}`);
    
    // Always update synthetic progress if we have valid progress
    if (targetProgress > 0) {
      setSyntheticProgress(prev => {
        const currentProgress = prev[videoId]?.progress || 5;
        const actualTarget = Math.max(targetProgress, 5);
        
        // Clear any existing interval for this video
        if (syntheticProgressIntervalsRef.current[videoId]) {
          clearInterval(syntheticProgressIntervalsRef.current[videoId]);
          delete syntheticProgressIntervalsRef.current[videoId];
        }
        
        // Clear any existing fallback interval
        if (fallbackProgressIntervalsRef.current[videoId]) {
          clearInterval(fallbackProgressIntervalsRef.current[videoId]);
          delete fallbackProgressIntervalsRef.current[videoId];
        }
        
        // If target is higher than current, start smooth animation
        if (actualTarget > currentProgress && actualTarget < 100) {
          // Create smooth animation to target progress
          const intervalId = setInterval(() => {
            setSyntheticProgress(prevState => {
              const current = prevState[videoId]?.progress || currentProgress;
              const target = prevState[videoId]?.targetProgress || actualTarget;
              
              // Gradually increment by small amounts (0.5-1.5% per 500ms)
              const increment = 0.5 + Math.random() * 1;
              const newProgress = Math.min(current + increment, target);
              
              // If we've reached the target, clear the interval
              if (newProgress >= target) {
                if (syntheticProgressIntervalsRef.current[videoId]) {
                  clearInterval(syntheticProgressIntervalsRef.current[videoId]);
                  delete syntheticProgressIntervalsRef.current[videoId];
                }
                
                // Start fallback progress after reaching target
                // This will kick in if backend doesn't update for 10+ seconds
                const fallbackIntervalId = setInterval(() => {
                  setSyntheticProgress(prevFallback => {
                    const currentFallback = prevFallback[videoId]?.progress || target;
                    const lastBackendUpdate = prevFallback[videoId]?.lastBackendUpdate || Date.now();
                    const timeSinceBackendUpdate = Date.now() - lastBackendUpdate;
                    
                    // Only continue fake progress if backend hasn't updated in 10+ seconds
                    // and we're not at 95% yet (cap fake progress at 95%)
                    if (timeSinceBackendUpdate > 10000 && currentFallback < 95) {
                      const fallbackIncrement = 0.2 + Math.random() * 0.3; // Slower fake progress
                      const newFallbackProgress = Math.min(currentFallback + fallbackIncrement, 95);
                      
                      // Update task display
                      setTasks(prevTasks => {
                        return prevTasks.map(task => {
                          if (task.videoId === videoId && task.status === 'analyzing') {
                            return {
                              ...task,
                              progress: Math.round(newFallbackProgress),
                              processingStatus: message || 'AI analyzing video content...',
                              uploadStatus: message || 'AI analyzing video content...'
                            };
                          }
                          return task;
                        });
                      });
                      
                      return {
                        ...prevFallback,
                        [videoId]: {
                          ...prevFallback[videoId],
                          progress: newFallbackProgress,
                          lastUpdate: Date.now()
                        }
                      };
                    }
                    
                    return prevFallback;
                  });
                }, 2000); // Check every 2 seconds for fallback progress
                
                fallbackProgressIntervalsRef.current[videoId] = fallbackIntervalId;
              }
              
              return {
                ...prevState,
                [videoId]: {
                  progress: newProgress,
                  lastUpdate: Date.now(),
                  targetProgress: target,
                  lastBackendUpdate: Date.now()
                }
              };
            });
            
            // Update the task display with the animated progress
            setTasks(prevTasks => {
              return prevTasks.map(task => {
                if (task.videoId === videoId && task.status === 'analyzing') {
                  const currentSynthetic = syntheticProgress[videoId]?.progress || currentProgress;
                  return {
                    ...task,
                    progress: Math.round(currentSynthetic),
                    processingStatus: message,
                    uploadStatus: message
                  };
                }
                return task;
              });
            });
          }, 500); // Update every 500ms for smooth animation
          
          syntheticProgressIntervalsRef.current[videoId] = intervalId;
        }
        
        // console.log(`✅ [UploadContext] Updating synthetic progress for ${videoId}: ${currentProgress}% -> ${actualTarget}%`);
        return { 
          ...prev, 
          [videoId]: { 
            progress: Math.max(actualTarget, currentProgress), // Don't go backwards
            lastUpdate: Date.now(),
            targetProgress: actualTarget,
            lastBackendUpdate: Date.now()
          } 
        };
      });
    }
    
    // ALWAYS update the task message and progress for the widget display
    setTasks(prev => {
      return prev.map(task => {
        if (task.videoId === videoId) {
          // console.log(`📝 [UploadContext] Updating task for ${videoId}: ${task.progress}% -> ${Math.max(targetProgress, 5)}%`);
          return {
            ...task,
            progress: Math.max(targetProgress, task.progress || 5), // Don't go backwards
            processingStatus: message,
            uploadStatus: message
          };
        }
        return task;
      });
    });
  }, [syntheticProgress]);

  const getSyntheticProgress = useCallback((videoId: string): number => {
    const progressData = syntheticProgress[videoId];
    return progressData?.progress || 5; // Return 5% minimum if no progress data
  }, [syntheticProgress]);

  const clearSyntheticProgress = useCallback((videoId: string) => {
    // Clear any interval if it exists
    if (syntheticProgressIntervalsRef.current[videoId]) {
      clearInterval(syntheticProgressIntervalsRef.current[videoId]);
      delete syntheticProgressIntervalsRef.current[videoId];
    }
    
    // Clear any fallback interval if it exists
    if (fallbackProgressIntervalsRef.current[videoId]) {
      clearInterval(fallbackProgressIntervalsRef.current[videoId]);
      delete fallbackProgressIntervalsRef.current[videoId];
    }
    
    setSyntheticProgress(prev => {
      const updated = { ...prev };
      
      // Clear any interval if it exists in state (legacy)
      if (updated[videoId]?.intervalId) {
        clearInterval(updated[videoId].intervalId);
      }
      
      delete updated[videoId];
      return updated;
    });
  }, []);

  // Function to clear all stuck states (user-facing reset button)
  const clearAllStuckStates = useCallback(() => {
    // console.log('🧹 User requested to clear all stuck states');
    
    // Clear all tasks
    setTasks([]);
    
    // Clear all synthetic progress
    setSyntheticProgress({});
    
    // Clear related localStorage entries
    const keysToRemove = [
      'upload_tasks',
      'processing_videos',
      'last_restoration_check',
      'has_recent_uploads'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear cache-related and progress-related entries
    Object.keys(localStorage).forEach(key => {
      if (key.includes('synthetic_progress') || 
          key.includes('video_recent_') || 
          key.includes('recent_task_') ||
          key.includes('video_cache') ||
          key.includes('cache_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    toast.success('Cleared all stuck states. Refreshing page...', {
      autoClose: 2000
    });
    
    // Refresh the page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }, []);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(syntheticProgress).forEach(entry => {
        if (entry.intervalId) {
          clearInterval(entry.intervalId);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTaskFromVideoStatus = useCallback((videoId: string, statusData: any) => {
    // console.log(`📊 [UploadContext] updateTaskFromVideoStatus called for ${videoId}:`, statusData);
    
    setTasks(prev => {
      return prev.map(task => {
        if (task.videoId !== videoId) return task;
        
        // console.log(`🎯 [UploadContext] Found task to update: ${task.fileName} (${task.id})`);
        // console.log(`📊 [UploadContext] Current task status: ${task.status}, progress: ${task.progress}%`);
        // console.log(`📨 [UploadContext] Received status data:`, statusData);
        
        let updates: Partial<UploadTask> = {};
        
        if (statusData.status === 'processing') {
          // Parse progress from error_message
          const { progress, message } = parseProgressFromMessage(statusData.error_message || '');
          // console.log(`🔍 [UploadContext] Parsed from error_message: ${progress}% - ${message}`);
          
          // Check if this is actually complete based on progress and message content
          const hasClips = statusData.clips_count && statusData.clips_count > 0;
          const lowerMessage = message.toLowerCase();
          
          // More strict completion detection - only mark as complete if:
          // 1. Progress is 100% OR
          // 2. Message explicitly says "complete" or "finished" AND progress >= 90 OR
          // 3. Has clips AND progress is 100%
          const isActuallyComplete = progress >= 100 || 
                                   (progress >= 90 && (lowerMessage.includes('analysis complete') || 
                                                      lowerMessage.includes('processing complete') ||
                                                      lowerMessage.includes('finished'))) ||
                                   (hasClips && progress >= 100) ||
                                   statusData.status === 'analyzed';
          
          if (isActuallyComplete) {
            // console.log(`🎉 [UploadContext] Video appears to be complete based on progress/message: ${progress}% - ${message}`);
            // Treat as completed even if backend status is still 'processing'
            updates = {
              status: 'completed',
              progress: 100,
              processingStatus: `Analysis complete - ${statusData.clips_count || 0} clips generated`,
              uploadStatus: `Analysis complete - ${statusData.clips_count || 0} clips generated`
            };
            
            // Clear synthetic progress
            clearSyntheticProgress(videoId);
            
            // Remove from global processing tracking since it's complete
            removeFromGlobalProcessingVideos(videoId);
            
            // Auto-remove completed task after a short delay to show completion briefly
            setTimeout(() => {
              setTasks(prev => prev.filter(t => t.videoId !== videoId));
              // console.log(`🗑️ [UploadContext] Auto-removed completed task for video ${videoId}`);
            }, 3000); // Remove after 3 seconds
          } else {
            // DIRECTLY use the backend progress for the widget - NO synthetic progress complications
            updates = {
              status: 'analyzing',
              progress: Math.max(progress, 5), // Use backend progress directly
              processingStatus: message,
              uploadStatus: message
            };
            
            // console.log(`📊 [UploadContext] DIRECT progress update for ${task.fileName}: backend=${progress}%, display=${Math.max(progress, 5)}%`);
          }
          
        } else if (statusData.status === 'analyzed') {
          // console.log(`✅ [UploadContext] Task completed for ${task.fileName}`);
          
          updates = {
            status: 'completed',
            progress: 100,
            processingStatus: `Analysis complete - ${statusData.clips_count || 0} clips generated`,
            uploadStatus: `Analysis complete - ${statusData.clips_count || 0} clips generated`
          };
          
          // Clear synthetic progress
          clearSyntheticProgress(videoId);
          
          // Remove from global processing tracking since it's complete
          removeFromGlobalProcessingVideos(videoId);
          
          // Auto-remove completed task immediately when already analyzed
          // (no need to show completion for already completed videos)
          setTimeout(() => {
            setTasks(prev => prev.filter(t => t.videoId !== videoId));
            // console.log(`🗑️ [UploadContext] Auto-removed completed task for video ${videoId}`);
          }, 1000); // Remove after 1 second for already analyzed videos
          
        } else if (statusData.status === 'error') {
          // console.log(`❌ [UploadContext] Task failed for ${task.fileName}`);
          
          updates = {
            status: 'error',
            error: statusData.error_message || 'Analysis failed',
            processingStatus: statusData.error_message || 'Analysis failed',
            uploadStatus: statusData.error_message || 'Analysis failed'
          };
          
          // Clear synthetic progress
          clearSyntheticProgress(videoId);
          
          // Remove from global processing tracking since it failed
          removeFromGlobalProcessingVideos(videoId);
        }
        
        const updatedTask = { ...task, ...updates };
        // console.log(`🔄 [UploadContext] Updated task:`, updatedTask);
        return updatedTask;
      });
    });
  }, [parseProgressFromMessage, clearSyntheticProgress]);

  // Function to detect and clear stuck tasks
  const clearStuckTasks = useCallback(async () => {
    // Only check stuck tasks if user is authenticated
    if (!currentUser) return;
    
    const now = Date.now();
    const stuckTasks = tasks.filter(task => {
      const taskAge = now - task.startTime.getTime();
      return (
        (task.status === 'analyzing' && task.videoId && taskAge > 180000) || // Analyzing stuck for more than 3 minutes
        (task.status === 'uploading' && taskAge > 900000) || // Uploading stuck for more than 15 minutes
        (taskAge > 86400000) // Any task older than 24 hours (stale cache)
      );
    });
    
    if (stuckTasks.length === 0) return;
    
    // console.log(`🔍 [UploadContext] Checking ${stuckTasks.length} potentially stuck tasks...`);
    
    for (const task of stuckTasks) {
      const taskAge = now - task.startTime.getTime();
      
      try {
        if (taskAge > 86400000) {
          // For tasks older than 24 hours, clear them automatically (stale cache)
          // console.log(`🧹 [UploadContext] Clearing stale task ${task.id} (${Math.round(taskAge / 3600000)}h old)`);
          setTasks(prev => prev.filter(t => t.id !== task.id));
          
          // Also clear any related synthetic progress
          if (task.videoId) {
            clearSyntheticProgress(task.videoId);
          }
          
          toast.info(`Cleared old processing task for ${task.fileName}`, {
            toastId: `clear-stale-${task.id}` // Prevent duplicate toasts
          });
          continue;
        }
        
        if (task.status === 'uploading') {
          // For uploading tasks stuck for more than 15 minutes, just remove them
          // console.log(`🗑️ [UploadContext] Removing stuck upload task ${task.id} after 15 minutes`);
          setTasks(prev => prev.filter(t => t.id !== task.id));
          toast.info(`Removed stuck upload for ${task.fileName} - upload timed out`);
          continue;
        }
        
        if (task.status === 'analyzing' && task.videoId) {
          // Try to get video status from backend
          const response = await fetch(`/api/v1/videos/${task.videoId}/status`, {
            headers: {
              'Authorization': `Bearer ${await (auth.currentUser?.getIdToken() || '')}`
            }
          });
          
          if (response.status === 404) {
            // Video doesn't exist in backend, remove task completely
            // console.log(`❌ [UploadContext] Video ${task.videoId} not found in backend, removing stuck task`);
            setTasks(prev => prev.filter(t => t.id !== task.id));
            toast.info(`Removed stuck upload task for ${task.fileName} - video not found in backend`);
          } else if (response.ok) {
            const statusData = await response.json();
            
            // Special handling for URL imports (YouTube, etc.) that might show "processing.mp4"
            if (task.fileName === 'processing.mp4' && task.fileSize === 0 && task.source === 'url') {
              // This is likely a URL import stuck on placeholder data
              // If backend shows the video is complete or has clips, update the task properly
              if (statusData.status === 'analyzed' || (statusData.clips_count && statusData.clips_count > 0)) {
                // Get the actual video data to update the task with real filename and size
                try {
                  const videoResponse = await fetch(`/api/v1/videos/${task.videoId}`, {
                    headers: {
                      'Authorization': `Bearer ${await (auth.currentUser?.getIdToken() || '')}`
                    }
                  });
                  
                  if (videoResponse.ok) {
                    const videoData = await videoResponse.json();
                    // Update task with real video data
                    setTasks(prev => prev.map(t => {
                      if (t.id === task.id) {
                        return {
                          ...t,
                          fileName: videoData.filename || videoData.metadata?.original_title || 'Imported Video',
                          fileSize: videoData.metadata?.file_size || 0,
                          status: 'completed',
                          progress: 100,
                          processingStatus: `Import complete - ${statusData.clips_count || 0} clips generated`,
                          uploadStatus: `Import complete - ${statusData.clips_count || 0} clips generated`
                        };
                      }
                      return t;
                    }));
                    continue; // Skip normal update since we handled it above
                  }
                } catch (videoError) {
                  // console.error('Error fetching video data for stuck URL import:', videoError);
                }
              }
            }
            
            // Update task with real status from backend (normal flow)
            updateTaskFromVideoStatus(task.videoId!, statusData);
          } else if (response.status === 401) {
            // Auth issue, skip this check
            // console.log(`🔐 [UploadContext] Auth required for video ${task.videoId}, skipping stuck check`);
          }
        }
      } catch (error) {
        // console.error(`Error checking stuck task ${task.id}:`, error);
        // If we can't check the status and it's been stuck for more than 7 minutes, remove it
        if (Date.now() - task.startTime.getTime() > 420000) { // 7 minutes
          // console.log(`🗑️ [UploadContext] Removing task ${task.id} after 7 minutes of being stuck`);
          setTasks(prev => prev.filter(t => t.id !== task.id));
          toast.info(`Removed stuck upload task for ${task.fileName} - unable to verify status`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, updateTaskFromVideoStatus, currentUser]);

  // Check for stuck tasks every minute
  useEffect(() => {
    const interval = setInterval(clearStuckTasks, 60000); // 1 minute
    return () => clearInterval(interval);
  }, [clearStuckTasks]);

  const contextValue: UploadContextType = {
    tasks,
    addTask,
    addUrlImportTask,
    addProcessingVideo,
    updateTask,
    updateTaskFromVideoStatus,
    removeTask,
    cancelTask,
    hasActiveTasks: tasks.some(task => task.status === 'uploading' || task.status === 'analyzing'),
    clearCompletedTasks,
    getSyntheticProgress,
    updateSyntheticProgress,
    clearSyntheticProgress,
    clearAllStuckStates
  };

  // Cleanup all synthetic progress intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all intervals when component unmounts
      Object.keys(syntheticProgressIntervalsRef.current).forEach(videoId => {
        if (syntheticProgressIntervalsRef.current[videoId]) {
          clearInterval(syntheticProgressIntervalsRef.current[videoId]);
        }
      });
      syntheticProgressIntervalsRef.current = {};
      
      // Clear all fallback intervals
      Object.keys(fallbackProgressIntervalsRef.current).forEach(videoId => {
        if (fallbackProgressIntervalsRef.current[videoId]) {
          clearInterval(fallbackProgressIntervalsRef.current[videoId]);
        }
      });
      fallbackProgressIntervalsRef.current = {};
    };
  }, []);

  return (
    <UploadContext.Provider value={contextValue}>
      {children}
    </UploadContext.Provider>
  );
};